<template>
  <SearchableModel ref="searchableModelRef"
                   v-bind="{ ...props, modelName: 'MarketingMessage' }">
    <template #default="{ models }">
      <div class="table-responsive">
        <table class="table table-hover">
          <thead class="tw:text-left tw:text-sm tw:bg-zinc-50">
            <tr>
              <th>{{ $t('Message') }}</th>
              <th class="tw:hidden tw:md:table-cell">{{ $t('Sending') }}</th>
              <th class="tw:hidden tw:md:table-cell">{{ $t('Sent') }}</th>
              <th>{{ $t('Status') }}</th>
              <th class="tw:hidden tw:lg:table-cell">{{ $t('Updated') }}</th>
              <th><span class="tw:sr-only">{{ $t('Actions') }}</span></th>
            </tr>
          </thead>
          <tbody v-for="group in byType(models)" :key="group.type" :data-group="group.type">
            <tr class="tw:bg-zinc-50">
              <th colspan="6" class="tw:py-1.5 tw:text-left tw:text-xs tw:font-medium tw:text-zinc-600">
                <span class="tw:inline-flex tw:items-center tw:gap-1.5">
                  <component :is="TYPES[group.type].icon" class="tw:size-3.5 tw:text-zinc-400" aria-hidden="true" />
                  {{ $t(TYPES[group.type].label) }}
                </span>
              </th>
            </tr>
            <MarketingMessageListRow v-for="message in group.messages" :key="message.id" :message="message"
                                     @deleted="searchableModelRef?.getResults()" />
          </tbody>
        </table>
      </div>
    </template>
  </SearchableModel>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import SearchableModel from '@/components/searchable-model/SearchableModel.vue'
import type { SearchableModelProps } from '@/components/searchable-model'
import { CalendarClock, Hand, Zap } from '@lucide/vue'
import MarketingMessageListRow from './MarketingMessageListRow.vue'
import type { ListedMessage } from './types'

/**
 * The messages of an event, searched and filtered, in a table grouped by type. The server
 * sorts them type after type, so a page holds its groups one after the other.
 */
const props = withDefaults(defineProps<Omit<SearchableModelProps, 'modelName'>>(), {
  availableFilters: () => [],
  baseFilters: () => ({}),
  loadOnStart: true,
  withSearch: true,
})

const TYPES = {
  automatic: { label: 'Automatic messages', icon: Zap },
  scheduled: { label: 'Scheduled messages', icon: CalendarClock },
  manual: { label: 'Manual messages', icon: Hand },
} as const

/** The messages of the page, a group for each run of the same type. */
const byType = (messages: ListedMessage[]): { type: ListedMessage['type']; messages: ListedMessage[] }[] => messages
  .reduce<{ type: ListedMessage['type']; messages: ListedMessage[] }[]>((groups, message) => {
    const last = groups.at(-1)

    if (last?.type === message.type) {
      last.messages.push(message)
    } else {
      groups.push({ type: message.type, messages: [message] })
    }

    return groups
  }, [])

const searchableModelRef = ref<InstanceType<typeof SearchableModel> | null>(null)
</script>
